<template>
  <el-dialog
    class="work-log-detail-dialog"
    title="日志详情"
    :visible.sync="dialogVisible"
    width="860px"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div v-loading="loading">
      <template v-if="detail && detail.id">
        <div v-if="focusMessage" class="message-context"><strong>{{ focusMessage.senderName }}</strong>：{{ focusMessage.content }}</div>
        <div class="work-log-paper">
          <div class="author-head">
            <div class="author-avatar">{{ shortName(detail.submitUserName) }}</div>
            <div class="author-main">
              <div class="author-name">{{ detail.submitUserName || '-' }}</div>
              <div class="author-meta">
                <span>{{ displayMinuteTime(detail.submitTime || detail.createTime) }}</span>
                <el-popover
                  v-if="receiverUsers.length"
                  placement="bottom-start"
                  trigger="click"
                  width="280"
                  :popper-append-to-body="false"
                >
                  <div class="receiver-popover-list">
                    <div v-for="user in receiverUsers" :key="user.id" class="receiver-popover-item">
                      <img v-if="user.avatar" class="receiver-popover-avatar" :src="user.avatar" />
                      <div v-else class="receiver-popover-avatar fallback">{{ shortName(user.userName) }}</div>
                      <span class="receiver-popover-name">{{ user.userName }}</span>
                    </div>
                  </div>
                  <span slot="reference" class="receiver-link">日志接收人 &gt;</span>
                </el-popover>
              </div>
            </div>
          </div>

          <div ref="bodyAnnotationHost" class="field-list body-annotation-host">
            <div ref="bodyAnnotationDrawing" class="body-annotation-drawing">
              <div v-for="field in renderFieldItems" :key="field.fieldKey || field._index" class="field-item">
                <div ref="fieldRows" class="field-row-content">
                  <div class="field-name">{{ field.fieldName }}</div>
                  <div v-if="field.fieldType === 'image'" class="image-grid">
                    <el-image
                      v-for="(file, index) in normalizeFileList(field.value)"
                      :key="file.uid || index"
                      class="image-card"
                      :src="file.url"
                      fit="cover"
                      :preview-src-list="previewImageList(field.value)"
                    >
                      <div slot="error" class="image-error">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                    </el-image>
                    <div v-if="!normalizeFileList(field.value).length" class="empty-value">-</div>
                  </div>
                  <div v-else-if="field.fieldType === 'file'" class="attachment-list">
                    <div
                      v-for="(file, index) in normalizeFileList(field.value)"
                      :key="file.uid || index"
                      class="attachment-item"
                      @click="openFile(file)"
                    >
                      <div class="attachment-icon">
                        <i :class="file.isImg ? 'el-icon-picture-outline' : 'el-icon-document'"></i>
                      </div>
                      <div class="attachment-body">
                        <div class="attachment-name">{{ file.name || '附件' }}</div>
                        <div class="attachment-meta">{{ formatFileSize(file.size) || file.contentType || '点击查看' }}</div>
                      </div>
                      <i class="el-icon-arrow-right attachment-arrow"></i>
                    </div>
                    <div v-if="!normalizeFileList(field.value).length" class="empty-value">-</div>
                  </div>
                  <div v-else class="field-value">{{ displayFieldValue(field) || '-' }}</div>
                </div>
                <div v-if="field.annotations && field.annotations.length" class="annotation-inline-list">
                  <div
                    v-for="annotation in field.annotations"
                    :key="`inline_${annotation.id}`"
                    :ref="`annotationCard_${annotation.id}`"
                    class="annotation-inline-card"
                    :class="{ active: String(activeAnnotationId) === String(annotation.id) }"
                    @click="selectAnnotation(annotation.id)"
                  >
                    <div class="annotation-inline-tail" :style="annotationInlineTailStyle(annotation)"></div>
                    <img v-if="annotation.annotationUserAvatar" class="annotation-inline-avatar" :src="annotation.annotationUserAvatar" />
                    <div v-else class="annotation-inline-avatar fallback">{{ shortName(annotation.annotationUserName) }}</div>
                    <div class="annotation-inline-body">
                      <div class="annotation-inline-main">
                        <span class="annotation-inline-user">{{ annotation.annotationUserName || '-' }}</span>
                        <span>：{{ annotation.content }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <svg v-if="bodyAnnotations.length" class="body-annotation-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                <template v-for="annotation in bodyAnnotations">
                  <polyline
                    v-for="(path, index) in annotationPreviewPaths(annotation)"
                    :key="`${annotation.id}-${index}`"
                    :points="path"
                    class="body-annotation-path"
                    :class="{ active: String(activeAnnotationId) === String(annotation.id) }"
                    fill="none"
                    @click.stop="selectAnnotation(annotation.id)"
                  />
                </template>
              </svg>
            </div>
          </div>

          <div class="comment-area">
            <div class="section-title">评论 {{ commentRows.length || '' }}</div>
            <div v-if="commentRows.length" class="comment-list">
              <div
                v-for="row in commentRows"
                :key="row.rowId"
                :ref="`message-${row.rowId}`"
                class="comment-item"
                :class="{ active: row.rowId === focusMessageRow || (row.annotationId && String(activeAnnotationId) === String(row.annotationId)) }"
              >
                <div class="comment-avatar-wrap">
                  <img v-if="row.avatar" class="comment-avatar-img" :src="row.avatar" />
                  <div v-else class="comment-avatar-fallback">{{ shortName(row.userName) }}</div>
                </div>
                <div class="comment-body">
                  <div class="comment-meta">
                    <span class="comment-user">{{ row.userName || '-' }}</span>
                    <span class="comment-time">{{ row.time || '-' }}</span>
                  </div>
                  <div class="comment-content">{{ row.content }}</div>
                  <div v-if="row.context" class="comment-context" @click="selectAnnotationAndScroll(row.annotationId)">
                    <div class="comment-context-text">{{ row.context }}</div>
                    <div class="comment-context-link">查看上下文</div>
                  </div>
                  <div v-if="row.type === 'annotation' && activeAnnotation && String(activeAnnotationId) === String(row.annotationId) && allowComment" class="annotation-reply-editor">
                    <el-input
                      v-model="annotationReplyTextMap[row.annotationId]"
                      size="mini"
                      placeholder="对该内容添加评论"
                      @keyup.enter.native="submitAnnotationReply(activeAnnotation)"
                    />
                    <el-button
                      type="primary"
                      size="mini"
                      :loading="annotationReplyLoading === row.annotationId"
                      @click="submitAnnotationReply(activeAnnotation)"
                    >发送</el-button>
                  </div>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无评论" :image-size="72"></el-empty>
            <div v-if="allowComment" class="comment-editor">
              <el-input
                v-model="commentContent"
                type="textarea"
                :rows="3"
                maxlength="1000"
                show-word-limit
                placeholder="请输入评论内容"
              />
              <div class="comment-actions">
                <el-button type="primary" size="mini" :loading="commentLoading" @click="submitComment">发表评论</el-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <span v-if="detail && detail.canEdit" slot="footer" class="dialog-footer">
      <el-button type="primary" @click="emitEdit">修改日志</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getWorkLogDetail, saveWorkLogAnnotationReply, saveWorkLogComment } from '@/api/crm/workLog'

const INLINE_CARD_STEP_PX = 38

export default {
  name: 'WorkLogDetailDialog',
  props: {
    focusMessage: { type: Object, default: null },
    visible: {
      type: Boolean,
      default: false
    },
    logId: {
      type: [Number, String],
      default: null
    },
    allowComment: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      commentLoading: false,
      annotationReplyLoading: null,
      activeAnnotationId: null,
      bodyAnnotationLayout: null,
      detail: {},
      commentContent: '',
      annotationReplyTextMap: {}
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    receiverUsers() {
      const list = (this.detail && Array.isArray(this.detail.receiverUsers)) ? this.detail.receiverUsers : []
      if (list.length) {
        return list.map((item, index) => {
          const userName = item.userName || item.nickName || item.name || ''
          return {
            id: item.userId || item.id || index,
            userName: userName || '-',
            avatar: item.avatar || item.headImage || item.wechatAvatar || ''
          }
        })
      }
      return String((this.detail && this.detail.receiverSummary) || '')
        .split(/[、,，]/)
        .map((name, index) => {
          const userName = name.trim()
          return userName ? { id: `summary-${index}`, userName, avatar: '' } : null
        })
        .filter(Boolean)
    },
    bodyAnnotations() {
      const annotations = (this.detail && this.detail.annotations) || []
      const fields = (this.detail && this.detail.fieldValues) || []
      const ranges = (this.bodyAnnotationLayout && this.bodyAnnotationLayout.ranges) || []
      return annotations.filter(item => this.isBodyAnnotation(item)).map(item => {
        const shape = this.parseAnnotationShape(item)
        const displayShape = this.transformShapeForDisplay(shape)
        return {
          ...item,
          _shape: shape,
          _displayShape: displayShape,
          anchorIndex: this.annotationAnchorIndex(shape, ranges, fields.length),
          context: this.annotationContext({ ...item, _shape: shape })
        }
      })
    },
    renderFieldItems() {
      const fields = ((this.detail && this.detail.fieldValues) || []).map((field, index) => ({
        ...field,
        _index: index,
        annotations: []
      }))
      if (!fields.length) {
        return []
      }
      this.bodyAnnotations.forEach(annotation => {
        const index = Math.max(0, Math.min(fields.length - 1, Number(annotation.anchorIndex || 0)))
        fields[index].annotations.push(annotation)
      })
      fields.forEach(field => {
        field.annotations.sort((a, b) => {
          const aBounds = this.annotationBounds(a) || {}
          const bBounds = this.annotationBounds(b) || {}
          return Number(aBounds.maxY || 0) - Number(bBounds.maxY || 0)
        })
        field.annotations = field.annotations.map((annotation, index) => ({
          ...annotation,
          _inlineOrder: index
        }))
      })
      return fields
    },
    activeAnnotation() {
      return this.bodyAnnotations.find(item => String(item.id) === String(this.activeAnnotationId)) || null
    },
    commentRows() {
      const rows = []
      ;((this.detail && this.detail.comments) || []).forEach(comment => {
        rows.push({
          rowId: `comment_${comment.id}`,
          type: 'comment',
          id: comment.id,
          userName: comment.commentUserName || '-',
          avatar: comment.commentUserAvatar || comment.avatar || comment.headImage || comment.wechatAvatar || '',
          time: comment.createTime || '-',
          content: comment.content || ''
        })
      })
      this.bodyAnnotations.forEach(annotation => {
        const context = `批注位置：${annotation.context || '日志正文'}`
        rows.push({
          rowId: `annotation_${annotation.id}`,
          type: 'annotation',
          id: annotation.id,
          annotationId: annotation.id,
          userName: annotation.annotationUserName || '-',
          avatar: annotation.annotationUserAvatar || '',
          time: annotation.createTime || '-',
          content: annotation.content || '',
          context
        })
        ;(annotation.replies || []).forEach(reply => {
          rows.push({
            rowId: `annotation_reply_${reply.id}`,
            type: 'annotationReply',
            id: reply.id,
            annotationId: annotation.id,
            userName: reply.replyUserName || '-',
            avatar: reply.replyUserAvatar || '',
            time: reply.createTime || '-',
            content: reply.content || '',
            context
          })
        })
      })
      return rows.sort((a, b) => this.timeValue(a.time) - this.timeValue(b.time))
    },
    focusMessageRow() {
      const m = this.focusMessage
      return m ? ({ workLogComment: 'comment_', workLogAnnotation: 'annotation_', workLogReply: 'annotation_reply_' }[m.sourceType] || '') + m.recordId : ''
    }
  },
  watch: {
    visible(value) {
      if (value && this.logId) {
        this.loadDetail()
      }
    },
    logId(value) {
      if (this.visible && value) {
        this.loadDetail()
      }
    }
  },
  methods: {
    loadDetail() {
      this.loading = true
      this.detail = {}
      getWorkLogDetail(this.logId)
        .then(response => {
          this.detail = (response && response.data) || {}
          if (this.detail.id) this.$emit('loaded', this.detail)
          this.commentContent = ''
          this.annotationReplyTextMap = {}
          this.bodyAnnotationLayout = null
          if (!this.bodyAnnotations.some(item => String(item.id) === String(this.activeAnnotationId))) {
            this.activeAnnotationId = null
          }
          this.$nextTick(() => {
            this.refreshBodyAnnotationLayout()
            const nodes = this.$refs[`message-${this.focusMessageRow}`]
            if (nodes && nodes[0]) nodes[0].scrollIntoView({ block: 'center', behavior: 'smooth' })
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    displayMinuteTime(value) {
      if (!value) {
        return '-'
      }
      return String(value).replace('T', ' ').slice(0, 16)
    },
    submitAnnotationReply(annotation) {
      if (!annotation || !annotation.id) {
        return
      }
      const content = (this.annotationReplyTextMap[annotation.id] || '').trim()
      if (!content) {
        this.$message.warning('请输入评论内容')
        return
      }
      this.annotationReplyLoading = annotation.id
      saveWorkLogAnnotationReply({
        annotationId: annotation.id,
        content
      })
        .then(() => {
          this.$message.success('评论成功')
          this.$set(this.annotationReplyTextMap, annotation.id, '')
          this.loadDetail()
          this.$emit('refresh')
        })
        .finally(() => {
          this.annotationReplyLoading = null
        })
    },
    submitComment() {
      if (!this.commentContent || !this.commentContent.trim()) {
        this.$message.warning('请输入评论内容')
        return
      }
      this.commentLoading = true
      saveWorkLogComment({
        logId: this.logId,
        content: this.commentContent.trim()
      })
        .then(() => {
          this.$message.success('评论成功')
          this.commentContent = ''
          this.loadDetail()
          this.$emit('refresh')
        })
        .finally(() => {
          this.commentLoading = false
        })
    },
    handleClose() {
      this.detail = {}
      this.commentContent = ''
      this.activeAnnotationId = null
    },
    emitEdit() {
      this.$emit('edit', this.detail)
      this.dialogVisible = false
    },
    displayFieldValue(field) {
      if (!field) {
        return ''
      }
      const value = field.displayValue !== undefined && field.displayValue !== null ? field.displayValue : field.value
      if (Array.isArray(value)) {
        return value.join('、')
      }
      return value === undefined || value === null ? '' : String(value)
    },
    normalizeFileList(value) {
      if (!value) {
        return []
      }
      const list = Array.isArray(value) ? value : []
      return list.map((item, index) => ({
        uid: item.uid || item.id || index,
        id: item.id || item.fileId || '',
        isImg: this.isImageFile(item),
        name: item.name || item.fileName || '附件',
        url: item.url || item.fileUrl || item.path || '',
        size: item.size,
        contentType: item.contentType || item.mimeType || ''
      }))
    },
    isImageFile(file) {
      const source = `${file.url || file.fileUrl || file.path || ''} ${file.name || file.fileName || ''} ${file.contentType || file.mimeType || ''}`.toLowerCase()
      return !!file.isImg || !!file.isImage || /^image\//.test(file.contentType || file.mimeType || '') || /\.(png|jpe?g|gif|webp|bmp|svg)(?:[?#]|$)/i.test(source)
    },
    previewImageList(value) {
      return this.normalizeFileList(value).filter(item => item.url).map(item => item.url)
    },
    formatFileSize(size) {
      const number = Number(size || 0)
      if (!number) {
        return ''
      }
      if (number < 1024) {
        return `${number}B`
      }
      if (number < 1024 * 1024) {
        return `${(number / 1024).toFixed(1)}KB`
      }
      return `${(number / 1024 / 1024).toFixed(1)}MB`
    },
    shortName(name) {
      const text = String(name || '')
      return text.length > 2 ? text.slice(text.length - 2) : (text || '无')
    },
    openFile(file) {
      if (file && file.url) {
        window.open(file.url)
      }
    },
    parseAnnotationShape(annotation) {
      if (!annotation || !annotation.shapeJson) {
        return { paths: [] }
      }
      if (typeof annotation.shapeJson === 'object') {
        return Array.isArray(annotation.shapeJson.paths) ? annotation.shapeJson : { paths: [] }
      }
      try {
        const parsed = JSON.parse(annotation.shapeJson)
        return parsed && Array.isArray(parsed.paths) ? parsed : { paths: [] }
      } catch (e) {
        return { paths: [] }
      }
    },
    isBodyAnnotation(annotation) {
      const shape = this.parseAnnotationShape(annotation)
      return annotation && (annotation.fieldKey === '__log_body__' || shape.scope === 'body')
    },
    selectAnnotation(id) {
      this.activeAnnotationId = id
    },
    selectAnnotationAndScroll(id) {
      this.selectAnnotation(id)
      this.$nextTick(() => {
        const ref = this.$refs[`annotationCard_${id}`]
        const target = Array.isArray(ref) ? ref[0] : ref
        if (target && target.scrollIntoView) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } else if (this.$refs.bodyAnnotationHost && this.$refs.bodyAnnotationHost.scrollIntoView) {
          this.$refs.bodyAnnotationHost.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    annotationCommentCount(annotation) {
      return ((annotation && annotation.replies) || []).length + 1
    },
    annotationPreviewPaths(annotation) {
      const shape = annotation._displayShape || this.transformShapeForDisplay(this.parseAnnotationShape(annotation))
      return (shape.paths || []).map(path => {
        const points = Array.isArray(path.points) ? path.points : []
        return points.map(point => `${Number(point.x || 0) * 100},${Number(point.y || 0) * 100}`).join(' ')
      }).filter(Boolean)
    },
    annotationInlineStyle(annotation) {
      const bounds = this.annotationBounds(annotation)
      if (!bounds) {
        return { display: 'none' }
      }
      return {
        top: `${Math.max(2, Math.min(96, bounds.maxY * 100 + 1.8))}%`
      }
    },
    annotationInlineTailStyle(annotation) {
      const bounds = this.shapeBounds(annotation && annotation._displayShape)
      if (!bounds) {
        return { display: 'none' }
      }
      const layout = this.bodyAnnotationLayout || {}
      const row = layout.rows && layout.rows[Math.max(0, Math.min(layout.rows.length - 1, Number(annotation.anchorIndex || 0)))]
      const centerX = ((bounds.minX + bounds.maxX) / 2) * 100
      let height = 34
      if (row && layout.hostHeight) {
        const shapeBottom = Number(bounds.maxY || 0) * Number(layout.hostHeight || 0)
        height = this.clampNumber(Number(row.displayBottom || 0) - shapeBottom + 8, 18, 170)
      }
      height = Math.round(height + Math.max(0, Number(annotation._inlineOrder || 0)) * INLINE_CARD_STEP_PX)
      return {
        left: `${this.clampNumber(centerX, 9, 91)}%`,
        top: `-${height}px`,
        height: `${height}px`
      }
    },
    clampNumber(value, min, max) {
      return Math.max(min, Math.min(max, Number(value || 0)))
    },
    annotationBounds(annotation) {
      const shape = annotation._shape || this.parseAnnotationShape(annotation)
      return this.shapeBounds(shape)
    },
    shapeBounds(shape) {
      const points = []
      ;((shape && shape.paths) || []).forEach(path => {
        if (path && Array.isArray(path.points)) {
          points.push(...path.points)
        }
      })
      if (!points.length) {
        return null
      }
      return points.reduce((result, point) => {
        const x = Math.max(0, Math.min(1, Number(point.x || 0)))
        const y = Math.max(0, Math.min(1, Number(point.y || 0)))
        return {
          minX: Math.min(result.minX, x),
          maxX: Math.max(result.maxX, x),
          minY: Math.min(result.minY, y),
          maxY: Math.max(result.maxY, y)
        }
      }, { minX: 1, maxX: 0, minY: 1, maxY: 0 })
    },
    annotationContext(annotation) {
      const shape = annotation._shape || this.parseAnnotationShape(annotation)
      if (shape.context && shape.context !== annotation.content) {
        return shape.context
      }
      const bounds = this.shapeBounds(shape)
      const fields = (this.detail && this.detail.fieldValues) || []
      if (!bounds || !fields.length) {
        return annotation.fieldName || '日志正文'
      }
      const ranges = (this.bodyAnnotationLayout && this.bodyAnnotationLayout.ranges) || []
      let index = Math.max(0, Math.min(fields.length - 1, Math.floor(((bounds.minY + bounds.maxY) / 2) * fields.length)))
      if (ranges.length) {
        const centerY = (bounds.minY + bounds.maxY) / 2
        let best = null
        let bestScore = -Infinity
        ranges.forEach(range => {
          const overlap = Math.max(0, Math.min(bounds.maxY, range.maxY) - Math.max(bounds.minY, range.minY))
          const score = overlap > 0 ? overlap + 1 : -Math.abs(centerY - ((range.minY + range.maxY) / 2))
          if (score > bestScore) {
            bestScore = score
            best = range
          }
        })
        if (best) {
          index = best.index
        }
      }
      const field = fields[index] || {}
      const content = this.excerpt(this.displayFieldValue(field), 80)
      return `${field.fieldName || '日志正文'}${content ? `：${content}` : ''}`
    },
    annotationAnchorIndex(shape, ranges, fieldCount) {
      const bounds = this.shapeBounds(shape)
      if (!bounds || !fieldCount) {
        return 0
      }
      if (Array.isArray(ranges) && ranges.length) {
        const anchorY = Math.max(0, Math.min(1, bounds.maxY))
        const match = ranges.find(range => anchorY <= range.maxY)
        if (match) {
          return Math.max(0, Math.min(fieldCount - 1, match.index))
        }
      }
      return Math.max(0, Math.min(fieldCount - 1, Math.floor(bounds.maxY * fieldCount)))
    },
    transformShapeForDisplay(shape) {
      const layout = this.bodyAnnotationLayout
      if (!shape || !Array.isArray(shape.paths) || !layout || !layout.rows || !layout.rows.length || !layout.textHeight || !layout.hostHeight) {
        return shape || { paths: [] }
      }
      return {
        ...shape,
        paths: shape.paths.map(path => ({
          points: (Array.isArray(path.points) ? path.points : []).map(point => {
            const textY = Math.max(0, Math.min(layout.textHeight, Number(point.y || 0) * layout.textHeight))
            let row = layout.rows[layout.rows.length - 1]
            for (let index = 0; index < layout.rows.length; index += 1) {
              const item = layout.rows[index]
              if (textY <= item.textBottom || index === layout.rows.length - 1) {
                row = item
                break
              }
            }
            const offset = Math.max(0, Math.min(row.height, textY - row.textTop))
            return {
              x: Math.max(0, Math.min(1, Number(point.x || 0))),
              y: Math.max(0, Math.min(1, (row.displayTop + offset) / layout.hostHeight))
            }
          })
        }))
      }
    },
    refreshBodyAnnotationLayout(retry = 0) {
      this.$nextTick(() => {
        const host = this.$refs.bodyAnnotationDrawing
        const rowRefs = this.$refs.fieldRows
        const rows = Array.isArray(rowRefs) ? rowRefs : (rowRefs ? [rowRefs] : [])
        if (!host || !rows.length) {
          this.bodyAnnotationLayout = null
          return
        }
        const hostRect = host.getBoundingClientRect()
        let textTop = 0
        const layoutRows = rows.map((row, index) => {
          const rect = row.getBoundingClientRect()
          const height = Math.max(1, rect.height || 1)
          const item = {
            index,
            height,
            textTop,
            textBottom: textTop + height,
            displayTop: Math.max(0, rect.top - hostRect.top),
            displayBottom: Math.max(0, rect.bottom - hostRect.top)
          }
          textTop += height
          return item
        })
        const textHeight = Math.max(1, textTop)
        layoutRows.forEach(row => {
          row.minY = Math.max(0, Math.min(1, row.textTop / textHeight))
          row.maxY = Math.max(0, Math.min(1, row.textBottom / textHeight))
        })
        this.bodyAnnotationLayout = {
          rows: layoutRows,
          ranges: layoutRows.map(row => ({ index: row.index, minY: row.minY, maxY: row.maxY })),
          textHeight,
          hostHeight: Math.max(1, hostRect.height || textHeight)
        }
        if (retry < 1) {
          this.$nextTick(() => this.refreshBodyAnnotationLayout(retry + 1))
        }
      })
    },
    excerpt(text, maxLength) {
      const value = String(text || '').replace(/\s+/g, ' ').trim()
      if (!value) {
        return ''
      }
      return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value
    },
    timeValue(value) {
      return Date.parse(String(value || '').replace(/-/g, '/')) || 0
    },
    periodText(row) {
      if (!row) {
        return '-'
      }
      if (row.logType === 'WEEKLY') {
        return [row.periodStartDate, row.periodEndDate].filter(Boolean).join(' 至 ') || '-'
      }
      return row.periodDate || '-'
    },
    statusText(status) {
      const map = { 1: '未提交', 2: '已提交', 3: '已撤回' }
      return map[Number(status)] || '-'
    },
    statusTag(status) {
      const map = { 1: 'info', 2: 'success', 3: 'warning' }
      return map[Number(status)] || 'info'
    }
  }
}
</script>

<style scoped>
.work-log-paper {
  max-width: 430px;
  margin: 0 auto;
}

.author-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0 16px;
}

.author-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #1677ff;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
}

.author-main {
  flex: 1;
  min-width: 0;
}

.author-name {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.author-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  color: #9ca3af;
  font-size: 13px;
  line-height: 18px;
}

.receiver-link {
  color: #5f6f86;
  cursor: pointer;
}

.receiver-popover-list {
  max-height: 260px;
  overflow: auto;
}

.receiver-popover-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px;
}

.receiver-popover-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  background: #4480f3;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
}

.receiver-popover-name {
  min-width: 0;
  overflow: hidden;
  color: #1f2937;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-list {
  border-top: 0;
}

.body-annotation-host {
  position: relative;
  padding: 0 0 20px;
}

.body-annotation-drawing {
  position: relative;
  padding: 8px 0 12px;
}

.body-annotation-svg {
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.body-annotation-path {
  stroke: #93c5fd;
  stroke-width: 0.8;
  stroke-linecap: round;
  stroke-linejoin: round;
  pointer-events: stroke;
  cursor: pointer;
  transition: stroke 0.15s ease, stroke-width 0.15s ease;
}

.body-annotation-path.active {
  stroke: #1677ff;
  stroke-width: 1.05;
  filter: drop-shadow(0 0 3px rgba(22, 119, 255, 0.35));
}

.field-item {
  position: relative;
  z-index: 1;
  padding: 15px 0 18px;
  border-bottom: 0;
}

.field-row-content {
  position: relative;
}

.field-name {
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
}

.field-value {
  padding: 0;
  border-radius: 0;
  background: transparent;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.8;
  color: #1f2937;
  font-size: 15px;
}

.annotation-inline-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 5px 8px;
  border: 1px solid #e6eefb;
  border-radius: 6px;
  background: #f2f6fc;
  color: #1f2937;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.04);
  box-sizing: border-box;
  cursor: pointer;
}

.annotation-inline-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0 0;
  box-sizing: border-box;
}

.annotation-inline-card.active {
  background: #eaf3ff;
  border-color: rgba(22, 119, 255, 0.28);
  box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.12), 0 6px 14px rgba(22, 119, 255, 0.1);
}

.annotation-inline-tail {
  position: absolute;
  left: 50%;
  top: -28px;
  width: 0;
  height: 28px;
  border-left: 1px dashed #93c5fd;
  pointer-events: none;
}

.annotation-inline-tail::before,
.annotation-inline-tail::after {
  position: absolute;
  left: -3px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #93c5fd;
  content: '';
}

.annotation-inline-tail::before {
  top: -1px;
}

.annotation-inline-tail::after {
  bottom: -1px;
}

.annotation-inline-card.active .annotation-inline-tail {
  border-left-color: #1677ff;
}

.annotation-inline-card.active .annotation-inline-tail::before,
.annotation-inline-card.active .annotation-inline-tail::after {
  background: #1677ff;
}

.annotation-inline-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  background: #1677ff;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
}

.annotation-inline-avatar.fallback {
  background: #4480f3;
}

.annotation-inline-body {
  flex: 1;
  min-width: 0;
}

.annotation-inline-main {
  color: #334155;
  font-size: 12px;
  line-height: 18px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  word-break: break-word;
}

.annotation-inline-user {
  color: #1f2937;
  font-weight: 700;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 10px;
}

.image-card {
  width: 100%;
  height: 96px;
  border-radius: 6px;
  background: #f3f6fa;
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #a8b0bd;
  font-size: 24px;
}

.attachment-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #edf1f7;
  border-radius: 6px;
  background: #fbfcfe;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.attachment-item:hover {
  border-color: #b9d6ff;
  box-shadow: 0 2px 8px rgba(47, 128, 237, 0.08);
}

.attachment-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #eef5ff;
  color: #2f80ed;
  font-size: 20px;
}

.attachment-body {
  min-width: 0;
  flex: 1;
}

.attachment-name {
  overflow: hidden;
  color: #1f2937;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-meta {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.attachment-arrow {
  flex: none;
  color: #b7beca;
}

.empty-value {
  color: #909399;
}

.annotation-reply-editor {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.annotation-reply-editor .el-input {
  flex: 1;
}

.comment-area {
  margin-top: 18px;
}

.section-title {
  margin-bottom: 10px;
  font-weight: 600;
  color: #303133;
}

.comment-item {
  display: flex;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eef1f6;
}

.comment-item.active {
  border-left: 3px solid #409eff;
  border-radius: 8px;
  background: #f8fbff;
}
.message-context { padding: 16px; margin-bottom: 16px; background: #ecf5ff; white-space: pre-wrap; overflow-wrap: anywhere; }

.comment-avatar-wrap {
  flex: none;
  width: 34px;
  height: 34px;
}

.comment-avatar-img,
.comment-avatar-fallback {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  box-sizing: border-box;
}

.comment-avatar-img {
  display: block;
  object-fit: cover;
}

.comment-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #4480f3;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  font-size: 12px;
}

.comment-user {
  min-width: 0;
  overflow: hidden;
  color: #606266;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.comment-time {
  flex: none;
  color: #909399;
}

.comment-content {
  white-space: pre-wrap;
  word-break: break-word;
  color: #303133;
}

.comment-context {
  margin-top: 8px;
  padding-left: 10px;
  border-left: 3px solid #e5e7eb;
  color: #8a94a6;
  font-size: 13px;
  line-height: 20px;
  cursor: pointer;
}

.comment-context-text {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.comment-context-link {
  margin-top: 2px;
  color: #3b82f6;
  font-weight: 600;
}

.comment-editor {
  margin-top: 12px;
}

.comment-actions {
  margin-top: 8px;
  text-align: right;
}
</style>
